import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Plus } from "@/lib/icons";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { useFileViewer } from "@/components/app/sending/file-viewer";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import {
  AffectedSeriesNotice,
  type AffectedSeries,
} from "./-components/chart/affected-series-notice";
import {
  authorizationToInput,
  authorizationToValues,
  blankAuthorization,
  type AuthorizationValues,
} from "./-components/authorizations/auth-form-model";
import { AuthorizationDialog } from "./-components/authorizations/authorization-dialog";
import { useOpenAuthorizationDocument } from "./-components/authorizations/authorization-documents";
import {
  AuthorizationItem,
  type AuthorizationRow,
} from "./-components/authorizations/authorization-item";
import {
  fileAuthorizationDocument,
  type PendingDocument,
} from "./-components/authorizations/pending-document";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/authorizations")({
  component: AuthorizationsTab,
});

const fail = (error: Error) => toast.error(errorWords(error));

type DialogState = { editing: AuthorizationRow | null; initial: AuthorizationValues };

/** MCO and Medicaid 2065D authorizations. Behind the money permission, matching the API: an
 * authorization carries the Medicaid client id, the service codes and the amounts billed. */
function AuthorizationsTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const canWrite = useCan("resident.financials:update", resident.facilityId);
  const queryClient = useQueryClient();
  const { openFile, fileViewer } = useFileViewer();
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [deleting, setDeleting] = useState<AuthorizationRow | null>(null);
  const [affected, setAffected] = useState<AffectedSeries[]>([]);

  const list = useQuery(
    orpc.care.residents.authorizations.list.queryOptions({ input: { residentId } }),
  );
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.care.residents.authorizations.key() });
  const viewDocument = useOpenAuthorizationDocument(openFile);

  const save = useMutation({
    mutationFn: async ({
      values,
      document,
    }: {
      values: AuthorizationValues;
      document: PendingDocument | null;
    }) => {
      const input = authorizationToInput(values);
      if (dialog?.editing) {
        const row = await orpc.care.residents.authorizations.update.call({
          authorizationId: dialog.editing.id,
          ...input,
        });
        // Shown rather than acted on: repricing a family's bill is somebody's decision.
        setAffected(row.affectedSeries);
        toast.success("Authorization updated");
        return;
      }
      const created = await orpc.care.residents.authorizations.create.call({
        residentId,
        ...input,
      });
      toast.success("Authorization added");
      if (!document) return;
      try {
        await fileAuthorizationDocument(created.id, document);
      } catch {
        toast.error(
          "Authorization was created but the document upload failed. You can upload it manually.",
        );
      }
    },
    onSuccess: async () => {
      setDialog(null);
      await invalidate();
    },
    onError: fail,
  });
  const setPrimary = useMutation(
    orpc.care.residents.authorizations.setPrimary.mutationOptions({
      onSuccess: async () => {
        toast.success("Primary authorization set");
        await invalidate();
      },
      onError: fail,
    }),
  );
  const remove = useMutation(
    orpc.care.residents.authorizations.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Authorization deleted");
        await invalidate();
      },
      onError: fail,
    }),
  );

  const add = (category: AuthorizationValues["authCategory"]) =>
    setDialog({ editing: null, initial: blankAuthorization(category) });

  return (
    <>
      <AffectedSeriesNotice affected={affected} onDismiss={() => setAffected([])} />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-4">
          <div>
            <CardTitle className="text-lg">Authorizations</CardTitle>
            <CardDescription>MCO and Medicaid 2065D authorizations</CardDescription>
          </div>
          {canWrite ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => add("mco")}>
                <Plus className="size-4" />
                Add MCO auth
              </Button>
              <Button variant="outline" onClick={() => add("medicaid_2065d")}>
                <Plus className="size-4" />
                Add 2065D auth
              </Button>
            </div>
          ) : null}
        </CardHeader>
        <CardContent>
          {list.isPending ? (
            <div className="space-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : list.isError ? (
            <ReadFailed
              message="Could not load the authorizations."
              error={list.error}
              onRetry={() => void list.refetch()}
              retrying={list.isFetching}
            />
          ) : !list.data?.length ? (
            <EmptyState
              title="No authorizations"
              description="Add MCO or Medicaid 2065D authorization records"
            />
          ) : (
            <Accordion type="multiple" className="space-y-4">
              {list.data.map((auth) => (
                <AuthorizationItem
                  key={auth.id}
                  auth={auth}
                  resident={resident}
                  residentId={residentId}
                  canWrite={canWrite}
                  settingPrimary={
                    setPrimary.isPending && setPrimary.variables?.authorizationId === auth.id
                  }
                  onSetPrimary={() => setPrimary.mutate({ authorizationId: auth.id })}
                  onEdit={() => setDialog({ editing: auth, initial: authorizationToValues(auth) })}
                  onDelete={() => setDeleting(auth)}
                  onView={(document) => viewDocument.mutate(document)}
                />
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {dialog ? (
        <AuthorizationDialog
          residentId={residentId}
          initial={dialog.initial}
          isEditing={dialog.editing !== null}
          pending={save.isPending}
          onSubmit={(values, document) => save.mutate({ values, document })}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {deleting ? (
        <RemoveConfirm
          title="Delete authorization"
          description={`Are you sure you want to delete authorization "${deleting.authorizationNumber}"? This will also delete all associated documents. This action cannot be undone.`}
          onConfirm={() => remove.mutate({ authorizationId: deleting.id })}
          onClose={() => setDeleting(null)}
        />
      ) : null}
      {fileViewer}
    </>
  );
}
